import { useEffect, useState } from 'react';
import { useGetList, useListContext } from 'react-admin';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Button,
  Autocomplete,
  TextField,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { BILLING_CLIENTS_STRINGS } from '../constants';

const CustomBillingClientsFilter = () => {
  const { setFilters, filterValues } = useListContext();

  const [selectedClient, setSelectedClient] = useState<any | null>(null);
  const [billingCycle, setBillingCycle] = useState('');
  const [showOnlyMissingGpId, setShowOnlyMissingGpId] = useState(false);
  const [showOnlyMissingBillingCycle, setShowOnlyMissingBillingCycle] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const { data: clientChoices = [], isLoading: isClientsLoading } = useGetList('BillingClientsList', {
    pagination: { page: 1, perPage: 2000 },
    sort: { field: 'billing_client_name', order: 'ASC' },
  });

  const uniqueClientChoices = Array.from(
    new Map(clientChoices.map((item) => [item.billing_client_number, item])).values()
  );

  const filteredOptions = uniqueClientChoices.filter(
    (option) =>
      option.billing_client_name.toLowerCase().startsWith(inputValue.toLowerCase()) ||
      (option.billing_client_number && option.billing_client_number.toLowerCase().includes(inputValue.toLowerCase()))
  );

  const applyFilters = () => {
    const newFilters: any = {
      billing_client_number: selectedClient?.billing_client_number || '',
      missing_gp_id: showOnlyMissingGpId,
      missing_billing_cycle: showOnlyMissingBillingCycle,
      billing_cycle: billingCycle || '',
    };

    if (filterValues.missing_info) {
      delete newFilters.missing_info;
    }

    setFilters(newFilters);
  };

  const clearFilters = () => {
    setSelectedClient(null);
    setInputValue('');
    setShowOnlyMissingGpId(false);
    setShowOnlyMissingBillingCycle(false);
    setBillingCycle('');
    setFilters({});
  };

  useEffect(() => {
    setShowOnlyMissingGpId(filterValues.missing_gp_id || false);
    setShowOnlyMissingBillingCycle(filterValues.missing_billing_cycle || false);
    setBillingCycle(filterValues.billing_cycle || '');
  }, [filterValues]);

  useEffect(() => {
    if (filterValues.missing_info) {
      setSelectedClient(null);
    }
  }, [filterValues.missing_info]);

  return (
    <Box display="flex" sx={{ width: 1400 }} alignItems="flex-end" gap={2} p={2} flexWrap="nowrap">
      <Autocomplete
        options={filteredOptions}
        getOptionLabel={(option) => `${option.billing_client_name} (${option.billing_client_number})`}
        isOptionEqualToValue={(option, value) => option.billing_client_number === value.billing_client_number}
        renderOption={(props, option) => (
          <li {...props} key={option.billing_client_number}>
            {`${option.billing_client_name} (${option.billing_client_number})`}
          </li>
        )}
        value={selectedClient}
        onChange={(event, newValue) => setSelectedClient(newValue)}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Client Name"
            size="small"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isClientsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        loading={isClientsLoading}
        filterOptions={(x) => x}
      />
      <FormControl size="small">
        <InputLabel>{BILLING_CLIENTS_STRINGS.LABELS.GP_ID_FILTER}</InputLabel>
        <Select
          value={showOnlyMissingGpId ? 'missing' : 'all'}
          onChange={(e) => setShowOnlyMissingGpId(e.target.value === 'missing')}
          label={BILLING_CLIENTS_STRINGS.LABELS.GP_ID_FILTER}
        >
          {BILLING_CLIENTS_STRINGS.FILTER_OPTIONS.GP_ID.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small">
        <InputLabel>{BILLING_CLIENTS_STRINGS.LABELS.BILLING_CYCLE_STATUS_FILTER}</InputLabel>
        <Select
          value={showOnlyMissingBillingCycle ? 'missing' : 'all'}
          onChange={(e) => setShowOnlyMissingBillingCycle(e.target.value === 'missing')}
          label={BILLING_CLIENTS_STRINGS.LABELS.BILLING_CYCLE_STATUS_FILTER}
        >
          {BILLING_CLIENTS_STRINGS.FILTER_OPTIONS.BILLING_CYCLE_STATUS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small">
        <InputLabel>{BILLING_CLIENTS_STRINGS.LABELS.BILLING_CYCLE}</InputLabel>
        <Select
          value={billingCycle}
          onChange={(e) => setBillingCycle(e.target.value)}
          label={BILLING_CLIENTS_STRINGS.LABELS.BILLING_CYCLE}
        >
          {BILLING_CLIENTS_STRINGS.FILTER_OPTIONS.BILLING_CYCLE.map((cycle) => (
            <MenuItem key={cycle} value={cycle}>
              {cycle}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Button
        variant="contained"
        onClick={applyFilters}
        startIcon={<SearchIcon />}
        sx={{
          minWidth: 100,
          height: 40,
          px: 2,
          borderRadius: 2,
          fontSize: '0.85rem',
          fontWeight: 500,
          textTransform: 'none',
        }}
      >
        {BILLING_CLIENTS_STRINGS.LABELS.APPLY}
      </Button>

      <Button
        variant="outlined"
        onClick={clearFilters}
        sx={{
          minWidth: 100,
          height: 40,
          px: 2,
          borderRadius: 2,
          fontSize: '0.85rem',
          fontWeight: 500,
          textTransform: 'none',
        }}
      >
        {BILLING_CLIENTS_STRINGS.LABELS.CLEAR}
      </Button>
    </Box>
  );
};

export default CustomBillingClientsFilter;
